import { buildUrl, getToken, UNAUTHORIZED_EVENT } from '@/lib/api';
import { API_CREDENTIALS } from '@/lib/apiBase';
import type {
  ImportResultDTO,
  RunMetaDTO,
  SuiteDetail,
  SuiteListItem,
  ValidateResult,
} from './types';

/**
 * 关键：这两个 wrapper 必须跟 web/src/lib/api.ts 的 jsonFetch 走同一套
 * 认证规则 —— 否则 cookie-less 路径（跨域 / 反代去 cookie / 用户用
 * `?token=` 进来的）一律 401，最先暴露在 LineToRuleModal 的 POST 保存上。
 * 复用 `getToken()` / `API_CREDENTIALS` / `UNAUTHORIZED_EVENT` 避免重复实现。
 */
function withAuthHeaders(init: RequestInit): RequestInit {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set('x-adh-token', token);
  return { credentials: API_CREDENTIALS, ...init, headers };
}

function notifyUnauthorized(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

async function request<T>(input: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(input, withAuthHeaders(init));
  if (res.status === 401) notifyUnauthorized();
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text();
    }
    const detail =
      body && typeof body === 'object' && 'error' in body
        ? (body as { error: { message?: string } }).error.message
        : typeof body === 'string'
          ? body
          : `HTTP ${res.status}`;
    const err = new Error(`checkpoint api ${res.status}: ${detail}`);
    (err as Error & { response?: unknown }).response = body;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function requestText(input: string, init: RequestInit = {}): Promise<string> {
  const res = await fetch(input, withAuthHeaders(init));
  if (res.status === 401) notifyUnauthorized();
  if (!res.ok) throw new Error(`checkpoint api ${res.status}`);
  return await res.text();
}

export const checkpointApi = {
  listSuites(): Promise<{ suites: SuiteListItem[] }> {
    return request(buildUrl('/api/checkpoint/suites'));
  },
  getSuite(id: string): Promise<SuiteDetail> {
    return request(buildUrl(`/api/checkpoint/suites/${encodeURIComponent(id)}`));
  },
  getSuiteYaml(id: string): Promise<string> {
    return requestText(buildUrl(`/api/checkpoint/suites/${encodeURIComponent(id)}/yaml`));
  },
  saveSuite(yaml: string): Promise<{ id: string; mtimeMs: number }> {
    return request(buildUrl('/api/checkpoint/suites'), {
      method: 'POST',
      headers: { 'Content-Type': 'text/yaml' },
      body: yaml,
    });
  },
  updateSuite(id: string, yaml: string): Promise<{ id: string; mtimeMs: number }> {
    return request(buildUrl(`/api/checkpoint/suites/${encodeURIComponent(id)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'text/yaml' },
      body: yaml,
    });
  },
  deleteSuite(id: string): Promise<{ ok: boolean }> {
    return request(buildUrl(`/api/checkpoint/suites/${encodeURIComponent(id)}`), {
      method: 'DELETE',
    });
  },
  validateYaml(yaml: string): Promise<ValidateResult> {
    return request(buildUrl('/api/checkpoint/suites/validate'), {
      method: 'POST',
      headers: { 'Content-Type': 'text/yaml' },
      body: yaml,
    });
  },
  importYaml(
    yaml: string,
    conflict: 'skip' | 'overwrite' | 'rename' = 'skip',
  ): Promise<ImportResultDTO> {
    return request(buildUrl('/api/checkpoint/suites/import', { conflict }), {
      method: 'POST',
      headers: { 'Content-Type': 'text/yaml' },
      body: yaml,
    });
  },
  exportUrl(id: string): string {
    return buildUrl(`/api/checkpoint/suites/${encodeURIComponent(id)}/export`);
  },
  listRuns(opts: { suiteId?: string; limit?: number } = {}): Promise<{ runs: { meta: RunMetaDTO }[] }> {
    return request(buildUrl('/api/checkpoint/runs', opts));
  },
  getRun(runId: string): Promise<{ meta: RunMetaDTO; snapshot: import('./types').RunMetaDTO['overallState'] | null }> {
    return request(buildUrl(`/api/checkpoint/runs/${encodeURIComponent(runId)}`));
  },
  getRunHits(runId: string): Promise<{ hits: import('./types').RunMetaDTO['overallState'][] }> {
    return request(buildUrl(`/api/checkpoint/runs/${encodeURIComponent(runId)}/hits`));
  },
  uploadRun(body: { meta: RunMetaDTO; snapshot: unknown; hits: unknown[]; lines?: unknown[] }): Promise<{ meta: RunMetaDTO }> {
    return request(buildUrl('/api/checkpoint/runs'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
};
